import React from "react";
import { useNavigate } from "react-router-dom";
import {
    GraduationCap,
    Handshake,
    ArrowRight,
    Mail,
    Phone,
    // Linkedin,
    // Instagram,
    // Facebook,
    MapPin,
    Sparkles,
    ChevronRight,
} from "lucide-react";

export default function Footer() {
    const navigate = useNavigate();

    const goTo = (path) => {
        navigate(path);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const goToJobs = () => goTo("/jobs");
    const goToTrainerRegister = () => goTo("/trainer-register");
    const goToPartnerRegister = () => goTo("/partner-register");

    return (
        <>
            <style>{`

                /* =====================================================
                   CERTED PREMIUM FOOTER
                ===================================================== */

                .certed-footer {
                    --purple: #5b21b6;
                    --purple-light: #8b5cf6;
                    --purple-dark: #16082f;
                    --gold: #e4da72;
                    --white: #ffffff;

                    position: relative;
                    width: 100%;
                    overflow: hidden;

                    color: var(--white);
                    background:
                        radial-gradient(
                            circle at 10% 5%,
                            rgba(139, 92, 246, 0.16),
                            transparent 28%
                        ),
                        radial-gradient(
                            circle at 90% 70%,
                            rgba(228, 218, 114, 0.055),
                            transparent 25%
                        ),
                        #0d051c;

                    font-family:
                        "Inter",
                        "Segoe UI",
                        Arial,
                        sans-serif;

                    box-sizing: border-box;
                }

                .certed-footer *,
                .certed-footer *::before,
                .certed-footer *::after {
                    box-sizing: border-box;
                }


                /* =====================================================
                   DECORATIVE BACKGROUND
                ===================================================== */

                .certed-footer-glow {
                    position: absolute;
                    width: 420px;
                    height: 420px;

                    border-radius: 50%;

                    background:
                        radial-gradient(
                            circle,
                            rgba(139, 92, 246, 0.13),
                            transparent 68%
                        );

                    pointer-events: none;
                    filter: blur(8px);
                }

                .certed-footer-glow-one {
                    top: -240px;
                    left: -180px;
                }

                .certed-footer-glow-two {
                    right: -220px;
                    bottom: 180px;
                }


                /* =====================================================
                   TOP CTA
                ===================================================== */

                .certed-footer-cta {
                    position: relative;
                    z-index: 2;

                    width: min(1180px, 92%);
                    min-height: 215px;

                    margin: 0 auto;

                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    gap: 45px;
                    padding: 48px 0;
                }

                .certed-footer-cta-content {
                    max-width: 690px;
                }

                .certed-footer-label {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;

                    margin-bottom: 13px;

                    color: var(--gold);

                    font-size: 10px;
                    font-weight: 900;

                    letter-spacing: 2px;
                    text-transform: uppercase;
                }

                .certed-footer-label::before {
                    content: "";

                    width: 24px;
                    height: 1px;

                    background: var(--gold);
                    opacity: 0.75;
                }

                .certed-footer-cta h2 {
                    margin: 0;

                    color: #ffffff;

                    font-family:
                        "Manrope",
                        "Inter",
                        "Segoe UI",
                        sans-serif;

                    font-size: clamp(
                        32px,
                        4vw,
                        46px
                    );

                    line-height: 1.1;

                    font-weight: 800;

                    letter-spacing: -1.5px;
                }

                .certed-footer-cta h2 span {
                    display: block;

                    margin-top: 5px;

                    color: var(--gold);
                }

                .certed-footer-cta p {
                    max-width: 610px;

                    margin: 16px 0 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.60
                        );

                    font-size: 13px;
                    line-height: 1.8;
                }


                /* =====================================================
                   CTA ACTIONS
                ===================================================== */

                .certed-footer-cta-actions {
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    gap: 11px;

                    flex-shrink: 0;
                }

                .certed-footer-primary-btn,
                .certed-footer-secondary-btn {
                    min-height: 49px;

                    display: inline-flex;
                    align-items: center;
                    justify-content: center;

                    gap: 8px;

                    padding: 0 20px;

                    border-radius: 10px;

                    font-family: inherit;
                    font-size: 12px;
                    font-weight: 800;

                    white-space: nowrap;

                    cursor: pointer;

                    transition:
                        transform 0.25s ease,
                        box-shadow 0.25s ease,
                        background 0.25s ease,
                        border-color 0.25s ease;
                }

                .certed-footer-primary-btn {
                    color: #ffffff;

                    background:
                        linear-gradient(
                            135deg,
                            #4c1d95,
                            #8b5cf6
                        );

                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.12
                        );

                    box-shadow:
                        0 12px 35px
                        rgba(
                            76,
                            29,
                            149,
                            0.30
                        );
                }

                .certed-footer-primary-btn:hover {
                    transform: translateY(-3px);

                    box-shadow:
                        0 18px 42px
                        rgba(
                            76,
                            29,
                            149,
                            0.45
                        );
                }

                .certed-footer-secondary-btn {
                    color: #ffffff;

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.045
                        );

                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.14
                        );

                    backdrop-filter: blur(10px);
                }

                .certed-footer-secondary-btn:hover {
                    transform: translateY(-3px);

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.09
                        );

                    border-color:
                        rgba(
                            228,
                            218,
                            114,
                            0.35
                        );
                }


                /* =====================================================
                   MAIN FOOTER
                ===================================================== */

                .certed-footer-main {
                    position: relative;
                    z-index: 2;

                    border-top:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.075
                        );

                    border-bottom:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.075
                        );

                    background:
                        linear-gradient(
                            180deg,
                            rgba(255,255,255,0.018),
                            rgba(255,255,255,0)
                        );
                }

                .certed-footer-container {
                    width: min(1180px, 92%);

                    margin: 0 auto;

                    padding: 60px 0;

                    display: grid;

                    grid-template-columns:
                        1.8fr
                        1fr
                        1fr
                        1fr
                        1fr;

                    gap: 40px;
                }


                /* =====================================================
                   BRAND
                ===================================================== */

                .certed-footer-brand {
                    max-width: 355px;
                }

                .certed-footer-logo {
                    width: fit-content;

                    display: inline-flex;
                    align-items: center;

                    gap: 11px;

                    cursor: pointer;
                    user-select: none;
                }

                .certed-footer-logo-icon {
                    position: relative;

                    width: 45px;
                    height: 45px;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    color: #ffffff;

                    background:
                        linear-gradient(
                            135deg,
                            #4c1d95,
                            #8b5cf6
                        );

                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.12
                        );

                    border-radius: 11px;

                    font-family:
                        "Manrope",
                        sans-serif;

                    font-size: 21px;
                    font-weight: 900;

                    box-shadow:
                        0 10px 30px
                        rgba(
                            76,
                            29,
                            149,
                            0.28
                        );
                }

                .certed-footer-logo-icon::after {
                    content: "";

                    position: absolute;

                    width: 7px;
                    height: 7px;

                    right: 7px;
                    top: 7px;

                    border-radius: 50%;

                    background: var(--gold);

                    box-shadow:
                        0 0 12px
                        rgba(
                            228,
                            218,
                            114,
                            0.55
                        );
                }

                .certed-footer-logo-text {
                    color: #ffffff;

                    font-family:
                        "Manrope",
                        "Inter",
                        sans-serif;

                    font-size: 27px;
                    font-weight: 900;

                    letter-spacing: -1.3px;
                }

                .certed-footer-logo-text span {
                    color: var(--gold);
                }

                .certed-footer-description {
                    margin: 19px 0 25px;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.57
                        );

                    font-size: 12.5px;

                    line-height: 1.85;
                }


                /* =====================================================
                   CONTACT
                ===================================================== */

                .certed-footer-contact {
                    display: flex;
                    flex-direction: column;

                    gap: 10px;
                }

                .certed-footer-contact a {
                    width: fit-content;

                    display: flex;
                    align-items: center;

                    gap: 10px;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.68
                        );

                    text-decoration: none;

                    font-size: 11.5px;

                    transition:
                        color 0.2s ease,
                        transform 0.2s ease;
                }

                .certed-footer-contact a:hover {
                    color: #ffffff;

                    transform:
                        translateX(3px);
                }

                .certed-footer-contact a span {
                    width: 32px;
                    height: 32px;

                    flex-shrink: 0;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    color: var(--gold);

                    background:
                        rgba(
                            139,
                            92,
                            246,
                            0.12
                        );

                    border:
                        1px solid
                        rgba(
                            139,
                            92,
                            246,
                            0.14
                        );

                    border-radius: 8px;
                }


                /* =====================================================
                   SOCIAL
                ===================================================== */

                .certed-footer-social {
                    display: flex;
                    align-items: center;

                    gap: 8px;

                    margin-top: 21px;
                }

                .certed-footer-social a {
                    width: 35px;
                    height: 35px;

                    display: flex;
                    align-items: center;
                    justify-content: center;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.70
                        );

                    background:
                        rgba(
                            255,
                            255,
                            255,
                            0.045
                        );

                    border:
                        1px solid
                        rgba(
                            255,
                            255,
                            255,
                            0.10
                        );

                    border-radius: 9px;

                    text-decoration: none;

                    transition:
                        color 0.2s ease,
                        background 0.2s ease,
                        transform 0.2s ease,
                        border-color 0.2s ease;
                }

                .certed-footer-social a:hover {
                    color: #ffffff;

                    background:
                        linear-gradient(
                            135deg,
                            #4c1d95,
                            #8b5cf6
                        );

                    border-color:
                        rgba(
                            255,
                            255,
                            255,
                            0.16
                        );

                    transform:
                        translateY(-3px);
                }


                /* =====================================================
                   FOOTER COLUMNS
                ===================================================== */

                .certed-footer-column {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                }

                .certed-footer-column h4 {
                    display: flex;
                    align-items: center;
                    gap: 7px;

                    margin:
                        2px 0 20px;

                    color: #ffffff;

                    font-size: 11px;

                    font-weight: 900;

                    letter-spacing: 1.6px;

                    text-transform: uppercase;
                }

                .certed-footer-column h4::before {
                    content: "";

                    width: 4px;
                    height: 15px;

                    border-radius: 4px;

                    background: var(--gold);
                }

                .certed-footer-column button {
                    position: relative;

                    width: fit-content;

                    display: inline-flex;
                    align-items: center;

                    gap: 6px;

                    margin-bottom: 13px;

                    padding: 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.57
                        );

                    background: transparent;

                    border: 0;

                    cursor: pointer;

                    font-family: inherit;

                    font-size: 11.5px;

                    text-align: left;

                    transition:
                        color 0.2s ease,
                        transform 0.2s ease;
                }

                .certed-footer-column button svg {
                    width: 13px;
                    height: 13px;

                    color: var(--gold);

                    opacity: 0;

                    transform:
                        translateX(-4px);

                    transition:
                        opacity 0.2s ease,
                        transform 0.2s ease;
                }

                .certed-footer-column button:hover {
                    color: #ffffff;

                    transform:
                        translateX(4px);
                }

                .certed-footer-column button:hover svg {
                    opacity: 1;

                    transform:
                        translateX(0);
                }


                /* =====================================================
                   FOOTER INFO STRIP
                ===================================================== */

                .certed-footer-info {
                    width: min(1180px, 92%);

                    margin: 0 auto;

                    padding: 15px 0 5px;

                    display: flex;
                    align-items: center;

                    gap: 20px;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.36
                        );

                    font-size: 10px;
                }

                .certed-footer-info-item {
                    display: inline-flex;
                    align-items: center;

                    gap: 6px;
                }

                .certed-footer-info-item svg {
                    color:
                        rgba(
                            228,
                            218,
                            114,
                            0.65
                        );
                }


                /* =====================================================
                   BOTTOM BAR
                ===================================================== */

                .certed-footer-bottom {
                    position: relative;
                    z-index: 2;

                    background: #08030f;
                }

                .certed-footer-bottom-container {
                    width: min(1180px, 92%);

                    min-height: 64px;

                    margin: 0 auto;

                    display: flex;
                    align-items: center;
                    justify-content: space-between;

                    gap: 20px;
                }

                .certed-footer-bottom-container p {
                    margin: 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.40
                        );

                    font-size: 10px;

                    line-height: 1.5;
                }

                .certed-footer-bottom-links {
                    display: flex;
                    align-items: center;

                    gap: 11px;
                }

                .certed-footer-bottom-links button {
                    padding: 0;

                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.42
                        );

                    background: transparent;

                    border: 0;

                    cursor: pointer;

                    font-family: inherit;

                    font-size: 10px;

                    transition:
                        color 0.2s ease;
                }

                .certed-footer-bottom-links button:hover {
                    color: var(--gold);
                }

                .certed-footer-bottom-links span {
                    color:
                        rgba(
                            255,
                            255,
                            255,
                            0.16
                        );

                    font-size: 8px;
                }


                /* =====================================================
                   LARGE TABLET
                ===================================================== */

                @media (max-width: 1080px) {

                    .certed-footer-container {
                        grid-template-columns:
                            1.5fr
                            1fr
                            1fr
                            1fr;

                        gap: 35px;
                    }

                    .certed-footer-brand {
                        grid-column: span 4;

                        max-width: 650px;
                    }

                    .certed-footer-cta {
                        align-items: flex-start;

                        flex-direction: column;

                        gap: 25px;

                        padding: 45px 0;
                    }

                    .certed-footer-cta-actions {
                        width: 100%;

                        justify-content: flex-start;
                    }
                }


                /* =====================================================
                   TABLET
                ===================================================== */

                @media (max-width: 760px) {

                    .certed-footer-cta {
                        width: 90%;

                        padding: 42px 0;
                    }

                    .certed-footer-cta h2 {
                        font-size: 33px;

                        letter-spacing: -1px;
                    }

                    .certed-footer-cta p {
                        font-size: 12px;

                        line-height: 1.75;
                    }

                    .certed-footer-cta-actions {
                        width: 100%;

                        display: grid;

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0, 1fr)
                            );

                        gap: 10px;
                    }

                    .certed-footer-primary-btn,
                    .certed-footer-secondary-btn {
                        width: 100%;
                    }

                    .certed-footer-container {
                        width: 90%;

                        padding: 48px 0;

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0, 1fr)
                            );

                        gap: 38px 25px;
                    }

                    .certed-footer-brand {
                        grid-column: span 2;

                        max-width: 100%;
                    }

                    .certed-footer-info {
                        width: 90%;

                        flex-wrap: wrap;

                        padding-bottom: 10px;
                    }

                    .certed-footer-bottom-container {
                        width: 90%;

                        min-height: auto;

                        padding: 18px 0;

                        flex-direction: column;

                        justify-content: center;

                        text-align: center;

                        gap: 10px;
                    }
                }


                /* =====================================================
                   MOBILE
                ===================================================== */

                @media (max-width: 480px) {

                    .certed-footer-cta {
                        width: 90%;

                        padding: 38px 0;
                    }

                    .certed-footer-label {
                        font-size: 9px;

                        letter-spacing: 1.6px;
                    }

                    .certed-footer-cta h2 {
                        font-size: 28px;

                        line-height: 1.15;
                    }

                    .certed-footer-cta p {
                        margin-top: 13px;

                        font-size: 11.5px;
                    }

                    .certed-footer-cta-actions {
                        grid-template-columns: 1fr;
                    }

                    .certed-footer-primary-btn,
                    .certed-footer-secondary-btn {
                        min-height: 46px;
                    }

                    .certed-footer-container {
                        width: 90%;

                        padding: 42px 0;

                        grid-template-columns: 1fr;

                        gap: 30px;
                    }

                    .certed-footer-brand {
                        grid-column: span 1;
                    }

                    .certed-footer-logo-text {
                        font-size: 24px;
                    }

                    .certed-footer-logo-icon {
                        width: 42px;
                        height: 42px;
                    }

                    .certed-footer-description {
                        font-size: 11.5px;

                        line-height: 1.8;
                    }

                    .certed-footer-column h4 {
                        margin-bottom: 16px;
                    }

                    .certed-footer-column button {
                        font-size: 11.5px;

                        margin-bottom: 12px;
                    }

                    .certed-footer-info {
                        width: 90%;

                        padding: 14px 0 5px;

                        flex-direction: column;

                        align-items: flex-start;

                        gap: 9px;
                    }

                    .certed-footer-bottom-container {
                        width: 90%;
                    }

                    .certed-footer-bottom-container p {
                        font-size: 9.5px;
                    }
                }


                /* =====================================================
                   EXTRA SMALL
                ===================================================== */

                @media (max-width: 350px) {

                    .certed-footer-cta h2 {
                        font-size: 25px;
                    }

                    .certed-footer-logo-text {
                        font-size: 22px;
                    }
                }


                /* =====================================================
                   REDUCED MOTION
                ===================================================== */

                @media (prefers-reduced-motion: reduce) {

                    .certed-footer *,
                    .certed-footer *::before,
                    .certed-footer *::after {
                        scroll-behavior: auto !important;

                        transition: none !important;

                        animation: none !important;
                    }
                }


                /* =====================================================
                   HIDE SCROLLBAR
                ===================================================== */

                .certed-footer {
                    scrollbar-width: none;
                }

                .certed-footer::-webkit-scrollbar {
                    display: none;
                }

            `}</style>


            {/* =====================================================
                FOOTER
            ===================================================== */}

            <footer className="certed-footer">

                {/* Decorative Background */}

                <div
                    className="certed-footer-glow certed-footer-glow-one"
                    aria-hidden="true"
                />

                <div
                    className="certed-footer-glow certed-footer-glow-two"
                    aria-hidden="true"
                />


                {/* =================================================
                    CTA
                ================================================= */}

                <section className="certed-footer-cta">

                    <div className="certed-footer-cta-content">

                        <span className="certed-footer-label">
                            <Sparkles size={12} />
                            Grow With CertED
                        </span>

                        <h2>
                            Let's Build Better
                            <span>
                                Learning Opportunities.
                            </span>
                        </h2>

                        <p>
                            Connect with trainers, institutions,
                            recruiters and organizations through
                            the CertED professional training ecosystem.
                        </p>

                    </div>


                    <div className="certed-footer-cta-actions">

                        <button
                            type="button"
                            className="certed-footer-primary-btn"
                            onClick={goToTrainerRegister}
                        >
                            <GraduationCap size={17} />

                            Join as Trainer

                            <ArrowRight size={16} />
                        </button>


                        <button
                            type="button"
                            className="certed-footer-secondary-btn"
                            onClick={goToPartnerRegister}
                        >
                            <Handshake size={17} />

                            Become a Partner
                        </button>

                    </div>

                </section>


                {/* =================================================
                    MAIN
                ================================================= */}

                <div className="certed-footer-main">

                    <div className="certed-footer-container">


                        {/* =============================================
                            BRAND
                        ============================================= */}

                        <div className="certed-footer-brand">

                            <div
                                className="certed-footer-logo"
                                onClick={() => goTo("/")}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === "Enter" ||
                                        e.key === " "
                                    ) {
                                        goTo("/");
                                    }
                                }}
                            >

                                <span className="certed-footer-logo-icon">
                                    C
                                </span>

                                <span className="certed-footer-logo-text">
                                    Cert<span>ED</span>
                                </span>

                            </div>


                            <p className="certed-footer-description">
                                A professional training ecosystem
                                connecting expertise, learning and
                                opportunity across institutions,
                                businesses and professionals.
                            </p>


                            {/* Contact */}

                            <div className="certed-footer-contact">

                                <a href="mailto:support@certedtechnologies.com">

                                    <span>
                                        <Mail size={15} />
                                    </span>

                                    support@certedtechnologies.com

                                </a>


                                <a href="tel:+919009015026">

                                    <span>
                                        <Phone size={15} />
                                    </span>

                                    +91 9009015026

                                </a>

                            </div>


                            {/* Social */}

                           <div className="certed-footer-social">

    {/* LinkedIn */}
    <a
        href="https://www.linkedin.com/company/certed-technologies/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="LinkedIn"
        className="certed-social-link"
    >
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="currentColor"
        >
            <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.44-2.14 2.94v5.67H9.34V8.98h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.61 0 4.27 2.38 4.27 5.47v6.29zM5.32 7.42a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM3.54 8.98h3.56v11.47H3.54V8.98z" />
        </svg>
    </a>

    {/* Instagram */}
    <a
        href="https://www.instagram.com/certedtechnologies/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Instagram"
        className="certed-social-link"
    >
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <rect x="3" y="3" width="18" height="18" rx="5" />
            <circle cx="12" cy="12" r="4" />
            <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none" />
        </svg>
    </a>

    {/* X / Twitter */}
    <a
        href="https://x.com/certedtech"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="X"
        className="certed-social-link"
    >
        <svg
            viewBox="0 0 24 24"
            width="17"
            height="17"
            fill="currentColor"
        >
            <path d="M18.9 2H22l-6.77 7.74L23.2 22h-6.24l-4.89-6.39L6.48 22H3.36l7.24-8.28L3 2h6.4l4.42 5.84L18.9 2zm-1.1 17.87h1.73L8.48 4.02H6.62L17.8 19.87z" />
        </svg>
    </a>

    {/* YouTube */}
    <a
        href="https://www.youtube.com/@certedtechnologies"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="YouTube"
        className="certed-social-link"
    >
        <svg
            viewBox="0 0 24 24"
            width="19"
            height="19"
            fill="currentColor"
        >
            <path d="M23.5 6.2a3.01 3.01 0 0 0-2.12-2.13C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.57A3.01 3.01 0 0 0 .5 6.2 31.2 31.2 0 0 0 0 12a31.2 31.2 0 0 0 .5 5.8 3.01 3.01 0 0 0 2.12 2.13C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.57a3.01 3.01 0 0 0 2.12-2.13A31.2 31.2 0 0 0 24 12a31.2 31.2 0 0 0-.5-5.8zM9.6 15.5v-7l6.2 3.5-6.2 3.5z" />
        </svg>
    </a>

    {/* Facebook */}
    <a
        href="https://www.facebook.com/certedtechnologies"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Facebook"
        className="certed-social-link"
    >
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="currentColor"
        >
            <path d="M14 8h3V4.5c-.52-.07-2.3-.22-4.25-.22-4.2 0-7.08 2.57-7.08 7.3V15H2v4h3.67v5h4.5v-5h3.73l.6-4h-4.33v-3.1c0-1.16.31-1.9 1.83-1.9z" />
        </svg>
    </a>

</div>

                        </div>


                        {/* =============================================
                            EXPLORE
                        ============================================= */}

                        <div className="certed-footer-column">

                            <h4>
                                Explore
                            </h4>

                            <button
                                type="button"
                                onClick={goToJobs}
                            >
                                <ArrowRight />
                                Find Jobs
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/freelance")
                                }
                            >
                                <ArrowRight />
                                Freelance Projects
                            </button>

                            <button
                                type="button"
                                onClick={goToTrainerRegister}
                            >
                                <ArrowRight />
                                Join as Trainer
                            </button>

                        </div>


                        {/* =============================================
                            PARTNERS
                        ============================================= */}

                        <div className="certed-footer-column">

                            <h4>
                                Partners
                            </h4>

                            <button
                                type="button"
                                onClick={goToPartnerRegister}
                            >
                                <ArrowRight />
                                Hire Trainers
                            </button>

                            <button
                                type="button"
                                onClick={goToPartnerRegister}
                            >
                                <ArrowRight />
                                Become a Partner
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/partner-network")
                                }
                            >
                                <ArrowRight />
                                Partner Network
                            </button>

                        </div>


                        {/* =============================================
                            PLATFORM
                        ============================================= */}

                        <div className="certed-footer-column">

                            <h4>
                                Platform
                            </h4>

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/about")
                                }
                            >
                                <ArrowRight />
                                About CertED
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/contact")
                                }
                            >
                                <ArrowRight />
                                Contact Us
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/privacy-policy")
                                }
                            >
                                <ArrowRight />
                                Privacy Policy
                            </button>

                        </div>


                        {/* =============================================
                            OPPORTUNITIES
                        ============================================= */}

                        <div className="certed-footer-column">

                            <h4>
                                Opportunities
                            </h4>

                            <button
                                type="button"
                                onClick={goToJobs}
                            >
                                <ArrowRight />
                                Training Jobs
                            </button>

                            <button
                                type="button"
                                onClick={goToTrainerRegister}
                            >
                                <ArrowRight />
                                Trainer Network
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/freelance")
                                }
                            >
                                <ArrowRight />
                                Freelance Work
                            </button>

                        </div>

                    </div>


                    {/* =============================================
                        INFO STRIP
                    ============================================= */}

                    <div className="certed-footer-info">

                        <span className="certed-footer-info-item">
                            <GraduationCap size={13} />
                            Professional Training Ecosystem
                        </span>

                        <span className="certed-footer-info-item">
                            <Handshake size={13} />
                            Trainers & Partners
                        </span>

                        <span className="certed-footer-info-item">
                            <MapPin size={13} />
                            Pan India Network
                        </span>

                    </div>

                </div>


                {/* =================================================
                    BOTTOM
                ================================================= */}

                <div className="certed-footer-bottom">

                    <div className="certed-footer-bottom-container">

                        <p>
                            © {new Date().getFullYear()} CertED
                            Technologies. All rights reserved.
                        </p>


                        <div className="certed-footer-bottom-links">

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/privacy-policy")
                                }
                            >
                                Privacy
                            </button>

                            <span>•</span>

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/terms")
                                }
                            >
                                Terms
                            </button>

                            <span>•</span>

                            <button
                                type="button"
                                onClick={() =>
                                    goTo("/contact")
                                }
                            >
                                Contact
                            </button>

                        </div>

                    </div>

                </div>

            </footer>
        </>
    );
}
